/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getClaimCategoriesSelector,
	getClaimPrioritiesSelector,
	getClaimSectionsSelector,
	getClaimsListSelector,
	getIsCategoriesLoadingSelector,
	getIsClaimsListLoadingSelector,
	getIsPrioritiesLoadingSelector,
	getIsSectionsLoadingSelector,
	getPaginationSelector,
	getTabsSelector,
} from './selectors';
import {
	getClaimCategoriesAction,
	getClaimPrioritiesAction,
	getClaimsCountAction,
	getClaimSectionsAction,
	getClaimsListAction,
	resetStateAction,
} from './actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const ClaimsContainerWrapper = ({
	onGetClaimsList,
	onGetClaimsCount,
	claimsList,
	isClaimsListLoading,
	pagination,
	tabs,
	onGetClaimSections,
	onGetClaimCategories,
	onGetClaimPriorities,
	sections,
	categories,
	priorities,
}) => {
	useInjectSaga({ key: 'claimsContainerSaga', saga });
	useInjectReducer({ key: 'claimsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	const status = pathname.split('/')[2];

	useEffect(() => {
		onGetClaimsList({ status, searchQuery: removeModalQuery(query.toString()) });
		onGetClaimsCount();
		onGetClaimCategories();
		onGetClaimSections();
		onGetClaimPriorities();
	}, [status]);

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetClaimsList({ status, searchQuery: query.toString() });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetClaimsList({ status, searchQuery: query.toString() });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetClaimsList({ status, searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(
				normalizeDataGridModel(filterModel, TABLE_COLUMNS(sections, categories, priorities)),
			);

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetClaimsList({ status, searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={tabs}
			pageContent={
				<DataGridComponent
					keyTable={status}
					headerText="Претензии"
					columns={TABLE_COLUMNS(sections, categories, priorities)}
					rows={claimsList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isClaimsListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	claimsList: getClaimsListSelector(),
	isClaimsListLoading: getIsClaimsListLoadingSelector(),
	pagination: getPaginationSelector(),
	tabs: getTabsSelector(),
	categories: getClaimCategoriesSelector(),
	priorities: getClaimPrioritiesSelector(),
	sections: getClaimSectionsSelector(),
	isCategoriesLoading: getIsCategoriesLoadingSelector(),
	isPrioritiesLoading: getIsPrioritiesLoadingSelector(),
	isSectionsLoading: getIsSectionsLoadingSelector(),
});

const mapDispatchToProps = {
	onGetClaimsList: getClaimsListAction,
	onResetState: resetStateAction,
	onGetClaimsCount: getClaimsCountAction,
	onGetClaimCategories: getClaimCategoriesAction,
	onGetClaimPriorities: getClaimPrioritiesAction,
	onGetClaimSections: getClaimSectionsAction,
};

const ClaimsContainer = connect(mapStateToProps, mapDispatchToProps)(ClaimsContainerWrapper);

ClaimsContainerWrapper.propTypes = {
	onGetClaimsList: PropTypes.func.isRequired,
	onGetClaimsCount: PropTypes.func.isRequired,
	onGetClaimCategories: PropTypes.func.isRequired,
	onGetClaimPriorities: PropTypes.func.isRequired,
	onGetClaimSections: PropTypes.func.isRequired,
	claimsList: PropTypes.arrayOf(PropTypes.object),
	isClaimsListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	tabs: PropTypes.arrayOf(PropTypes.object),
	categories: PropTypes.arrayOf(PropTypes.object).isRequired,
	priorities: PropTypes.arrayOf(PropTypes.object).isRequired,
	sections: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ClaimsContainer;
